import { PICKED_TRAVEL_CALC_CATEGORY_CONST } from "@/src/components/ProposalTable/lib/const";

export interface TravelPremiumMismatch {
  pickedTravelCalculationCategory: string;
  finalPremium: unknown;
  premium: unknown;
  pickedCategoryPremium: unknown;
  premiumHelp: unknown;
  premiumFinancialRisk: unknown;
  premiumAccident: unknown;
  premiumCivil: unknown;
  insuredCountAgeFrom0To3: unknown;
  insuredCountAgeFrom4To10: unknown;
  insuredCountAgeFrom11To64: unknown;
  insuredCountAgeFrom65To69: unknown;
  insuredCountAgeFrom70To74: unknown;
}

/**
 * Detects the stale/inconsistent travel premium behind the premium-undercharge defect:
 * the top-level `premium` (what billing ultimately charges) no longer matches the freshly
 * recalculated `${pickedCategory}_premium`. This happens when a pricing factor (insuredCount,
 * age buckets, category) changes after PickTravelTariffCategoryAction last copied the premium,
 * leaving the top-level value stale and lower than the contract terms.
 *
 * Pure and side-effect free so it can be unit-tested and called safely before navigation.
 * Returns mismatch diagnostics, or `null` when the premium is consistent OR this is not a
 * travel flow (no picked category) — both of which must NOT block the user.
 *
 * The comparison is on the BASE `premium` vs `${pickedCategory}_premium` on purpose: promocode
 * and cross discounts only lower `finalPremium`, never the base, so a legitimate discount does
 * not trip the guard. (Mirrors the server-side guard in CreateBillingService.)
 */
export function getTravelPremiumMismatch(
  values: Record<string, any>
): TravelPremiumMismatch | null {
  const pickedCategory = values[PICKED_TRAVEL_CALC_CATEGORY_CONST];
  if (!pickedCategory) {
    return null;
  }

  const premium = values["premium"];
  const pickedCategoryPremium = values[`${pickedCategory}_premium`];

  if (pickedCategoryPremium && premium && pickedCategoryPremium !== premium) {
    return {
      pickedTravelCalculationCategory: pickedCategory,
      finalPremium: values["finalPremium"],
      premium,
      pickedCategoryPremium,
      premiumHelp: values[`${pickedCategory}_Premium_HELP(ASSISTANCE)`],
      premiumFinancialRisk: values[`${pickedCategory}_Premium_FinancialRisk`],
      premiumAccident: values[`${pickedCategory}_Premium_Accident`],
      premiumCivil: values[`${pickedCategory}_Premium_Civil`],
      insuredCountAgeFrom0To3: values["insuredCountAgeFrom0To3"],
      insuredCountAgeFrom4To10: values["insuredCountAgeFrom4To10"],
      insuredCountAgeFrom11To64: values["insuredCountAgeFrom11To64"],
      insuredCountAgeFrom65To69: values["insuredCountAgeFrom65To69"],
      insuredCountAgeFrom70To74: values["insuredCountAgeFrom70To74"],
    };
  }

  return null;
}
